import { BulkEditResponse } from '@core/models/interfaces/storage-data/bulk-edit';

/**
 * Transaction rules (POC-8664). A rule is a set of `criteria` a transaction has
 * to match and a set of `actions` to perform on the ones that do. Rules apply to
 * external transactions only — cash is never touched — and a manual edit always
 * outranks a rule.
 */

// ─── Criteria ────────────────────────────────────────────────────────────────

export type RuleCriterionType =
  | 'merchants'
  | 'amounts'
  | 'categories'
  | 'accounts';

/**
 * One rule matches on the merchant **or** on the original statement, never on
 * both: combining them takes two separate rules.
 */
export type RuleMerchantFieldType = 'merchant' | 'payeeName';
export type RuleMerchantConditionId = 'equals' | 'contains';

export type RuleAmountFieldType = 'expense' | 'income';
export type RuleAmountConditionId =
  | 'equals'
  | 'greaterThan'
  | 'lessThan'
  | 'between';

export interface RuleMerchantCondition {
  id: RuleMerchantConditionId;
  value: string;
}

export interface RuleMerchantsCriterion {
  type: 'merchants';
  params: {
    fieldType: RuleMerchantFieldType;
    conditions: RuleMerchantCondition[];
  };
}

/** `between` carries two bounds in `values`; every other operator one `value`. */
export type RuleAmountCondition =
  | { id: Exclude<RuleAmountConditionId, 'between'>; value: number }
  | { id: 'between'; values: [number, number] };

export interface RuleAmountsCriterion {
  type: 'amounts';
  params: {
    fieldType: RuleAmountFieldType;
    conditions: RuleAmountCondition[];
  };
}

/** Identifiers, never display names. Which identifier is per criterion type. */
export interface RuleOneOfCondition {
  id: 'oneOf';
  values: string[];
}

/**
 * `fieldType` is absent from the spec's snippet but the server does send it,
 * measured on a live account. The name misleads: `categoryName` carries the
 * category **id** — every observed value matched `categories.collection[].id`
 * and none matched a category name.
 */
export interface RuleCategoriesCriterion {
  type: 'categories';
  params: {
    fieldType: 'categoryName';
    conditions: RuleOneOfCondition[];
  };
}

/**
 * Also undocumented and also measured. The values are the `aid` field of the
 * accounts container, not `id` — that container uses abbreviated keys and has no
 * `id` at all.
 */
export interface RuleAccountsCriterion {
  type: 'accounts';
  params: {
    fieldType: 'accountId';
    conditions: RuleOneOfCondition[];
  };
}

export type RuleCriterion =
  | RuleMerchantsCriterion
  | RuleAmountsCriterion
  | RuleCategoriesCriterion
  | RuleAccountsCriterion;

// ─── Actions ─────────────────────────────────────────────────────────────────

/**
 * `goals` is out of the web client's first release and `split` is not built at
 * all, but both are part of the entity and can reach this client on a rule
 * created from a mobile app. They stay in the union so such a rule parses
 * instead of breaking the dashboard; anything unrecognised must be ignored on
 * render rather than assumed absent.
 */
export type RuleActionId =
  | 'merchant'
  | 'category'
  | 'hashtags'
  | 'goals'
  | 'doNotCount';

export type RuleAction =
  | { id: 'merchant'; value: string }
  | { id: 'category'; value: string }
  /** Each value keeps its leading `#`, as measured on a live account. */
  | { id: 'hashtags'; values: string[] }
  | { id: 'goals'; values: string[] }
  | { id: 'doNotCount'; value: boolean };

// ─── Entity and container ────────────────────────────────────────────────────

export interface Rule {
  /** A number on the wire (`1476`), where the spec's snippet shows a string. */
  id: string | number;
  name: string;
  criteria: RuleCriterion[];
  actions: RuleAction[];
  /**
   * Whether the user knows this rule exists. Rules created in the background —
   * by migration, or by "Apply for all" on transaction details — arrive `false`
   * and carry a red indicator until the user clicks them.
   */
  acknowledged: boolean;
  createdAt: number;
}

/** The `rules` container of `sync/fetchData`. */
export interface Rules {
  collection: Rule[];
  metaData: { syncAnchor: string } | null;
}

// ─── Requests ────────────────────────────────────────────────────────────────

export interface SaveRulePayload {
  /** Generated client-side, exactly as in bulk edit. */
  editSessionId: string;
  /** Present when editing an existing rule, absent when creating one. */
  id?: string;
  name: string;
  criteria: RuleCriterion[];
  actions: RuleAction[];
  applyToExisting: boolean;
  acknowledged: boolean;
  /**
   * Sent as `true` only on the retry after the user has seen the conflicts and
   * chosen to continue. The server skips conflict detection then.
   */
  bypassConflicts?: boolean;
}

/**
 * A rule the new one conflicts with. `criteria` and `actions` hold only the
 * conflicting entries, but each entry is complete — a merchant criterion that
 * overlaps on one value still arrives with all of its values.
 *
 * `id` arrives as a number from the dev API (`1476`), where the spec's snippet shows
 * a string, so it is typed as both.
 */
export interface RuleConflict {
  id: string | number;
  name: string;
  criteria: RuleCriterion[];
  actions: RuleAction[];
}

/** `rules/save` refused the rule because it overlaps existing ones. */
export const RULE_CONFLICT_ERROR_CODE = -1091004;

/**
 * Measured against the dev API on 2026-08-13: `data` is the array of conflicts
 * itself, where the spec's snippet wraps it in `{ conflicts: [...] }`. Both shapes
 * are accepted — reading only the documented one produced an empty conflict screen.
 */
// Not a DefaultResponse: the conflicts endpoint answers an array (or a
// wrapper) where DefaultResponse promises a record.
export interface RuleConflictResponse {
  status: 'error';
  code: number;
  data: RuleConflict[] | { conflicts: RuleConflict[] } | null;
}

/** The conflicts out of either shape. */
export const ruleConflictsOf = (
  response: RuleConflictResponse
): RuleConflict[] => {
  const data = response.data;
  if (Array.isArray(data)) return data;

  return data?.conflicts ?? [];
};

/**
 * `rules/save` answers with the bulk-edit envelope, because applying a rule to
 * existing transactions *is* a bulk edit and can run in the background over
 * thousands of rows.
 *
 * Measured 2026-08-13: with `applyToExisting: false` the dev API creates the rule and
 * answers `{status: 'success', data: null}` — there is no edit to poll, so there is no
 * session to report.
 */
export type SaveRuleResponse = BulkEditResponse | RuleConflictResponse;

export const isRuleConflictResponse = (
  response: SaveRuleResponse
): response is RuleConflictResponse =>
  response?.status === 'error' && response?.code === RULE_CONFLICT_ERROR_CODE;
